import React from "react";
import get from "lodash/get";
import find from "lodash/find";
import sortPrograms from "../../../utils/sort-programs";

import BuildProgramStepOneModal from "../../../parts/modals/add-cycle";
import BuildProgramStepTwoModal from "../../coaching-portal/common-components/cp-program-settings";

import LearnMoreModal from "../../../parts/modals/learn-more";
import EditProgramSettingsModal from "../../../parts/modals/edit-program-settings";
import SendModal from "../../coaching-portal/common-components/SendModal";

import FetchModal from "../../coaching-portal/common-components/FetchModal";
import ConfirmPushModal from "./components/ConfirmPush";
import Programs from "./Programs";
import { getRoleNameById } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { canLoadAllOrganizationAthletesForTasks } from "../../../utils/organization-permissions-helpers";
import { fetchSendProjection } from "../../../utils/projection-helpers";
import { loadCopiableCycles } from "../../../utils/copiable-cycle";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import TeamInviteModal from "../../coaching-portal/teams/components/TeamInviteModal";
import StartFlexibleProgram from "./components/StartFlexibleProgram";

const pageSize = 40;

const ProgramsController = async ({ res, req, locals, load, api }) => {
  const { query = {} } = req;
  const proxyAthlete = query.athlete;
  const page = query.page - 1 || 0;
  const loadPromises = [
    load("athleteCycles", {
      proxyAthlete,
      customLimit: pageSize,
      customSkip: pageSize * page,
      status: query["program-status"],
    }),
    load("athleteCyclesCount", proxyAthlete),
    load("athleteTeam", { proxyAthlete }),
    load("athleteFlexibleProgram", { proxyAthlete }),
    load("teamPrograms"),
  ];

  const organizationRoles = locals.organizationRoles || [];
  const hasOrganizationId = !!locals.activeUser.organizationId;
  //Get the name of the organization's role of the current user
  const organizationRoleName = locals.activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, locals.activeUser.organizationRoleId)
    : "Coach";
  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsAthletes =
    canLoadAllOrganizationAthletesForTasks(organizationRoleName);

  if (
    hasOrganizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  let program = null;

  const modal = get(query, "modal", {});

  let queryEditCycleSettings = {
    cycle: query.cycleId,
    proxyAthlete,
  };

  let queryEditCycleConfirmPush = {
    cycle: query.cycle,
    proxyAthlete,
  };

  if (proxyAthlete && hasOrganizationId) {
    queryEditCycleSettings = {
      ...queryEditCycleSettings,
      athleteFromOrganization: proxyAthlete,
    };

    queryEditCycleConfirmPush = {
      ...queryEditCycleConfirmPush,
      athleteFromOrganization: proxyAthlete,
    };
  }

  switch (modal.type) {
    case "edit-cycle-settings":
      loadPromises.push(load("editCycle", queryEditCycleSettings));
      break;
    case "confirm-push":
      await load("editCycle", queryEditCycleConfirmPush);
      const cycle = locals.editCycle
        ? locals.editCycle
        : find(locals.libraryCycles, { _id: locals.params.cycleId });

      const programPosition = query.program ? query.program - 1 : 0;
      program = cycle.programs[programPosition];
      break;
    case "edit-program-settings":
      loadPromises.push(load("editCycle", queryEditCycleSettings));
      break;
    case "learn-more":
      loadPromises.push(
        load("editCycle", queryEditCycleSettings),
        load("programExercises", { programId: query.cycleId })
      );
      break;
    case "fetch":
      loadPromises.push(
        loadCopiableCycles(
          { api, locals },
          query.fetchSourceOwner || locals.activeUserToRequest._id
        )
      );
      break;
    case "send":
      loadPromises.push(
        load("athleteCycles", { customProjection: fetchSendProjection }),
        load("clientsOnProgram")
      );
      if (query.recipient)
        loadPromises.push(load("unPushedAthleteCyclesNames", query.recipient));
      break;
    default:
      break;
  }

  await Promise.all(loadPromises);
  locals.activeCycles = locals.athleteCycles;

  if (query.pushedCycle && query.pushedProgram) {
    locals.activeCycles = locals.activeCycles.map((ac) => {
      if (ac._id === query.pushedCycle) {
        ac.status = "pushed";
        if (ac.programs) {
          ac.programs = ac.programs.map((p) => {
            if (p.order === query.pushedProgram) {
              p.status = "pushed";
            }
            return p;
          });
        }
      }
      return ac;
    });
  }

  locals.modal = {
    "build-step-one": <BuildProgramStepOneModal />,
    "build-step-two": <BuildProgramStepTwoModal />,
    "confirm-push": (
      <ConfirmPushModal
        fullProgram={program}
        athlete={locals.athlete}
        clientOnProgram={
          locals.athleteTeam?.length
            ? "team"
            : locals.athleteFlexibleProgram?.length
              ? "flexible"
              : null
        }
      />
    ),
    "edit-cycle-settings": <BuildProgramStepOneModal />,
    "edit-program-settings": <EditProgramSettingsModal />,
    "learn-more": <LearnMoreModal />,
    fetch: (
      <FetchModal
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    send: (
      <SendModal
        isAllowToViewAllOrganizationsAthletes={
          isAllowToViewAllOrganizationsAthletes
        }
      />
    ),
    "activity-feed": <ActivityDrawer />,
    "invite-to-team": <TeamInviteModal locals={locals} />,
    "start-flexible-program": <StartFlexibleProgram locals={locals} />,
  }[modal.type];
  query.sort = query.sort || "createdDate";
  locals.cards = sortPrograms(
    locals.activeCycles,
    query.sort,
    query["program-type"],
    query["program-progress"],
    query["program-view"]
  );
  res.body = (
    <Programs
      cards={locals.cards}
      total={locals.athleteCyclesCount.count}
      pageSize={pageSize}
    />
  );
};

export default ProgramsController;
